import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Loader2, X, Camera } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader, InputDecimal, InputInteger, FipeSelector } from '../../components/ui';
import { Panel, buttonClass } from '../../design-system';
import { veiculoStatusLabels } from '../../constants';

const inputClass =
  'w-full h-10 rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 focus:outline-none';
const labelClass = 'block text-sm font-medium text-slate-700 mb-1';

// Helper para campos numéricos opcionais (converte string vazia/null para undefined)
const optionalNumber = z.preprocess(
  (val) => (val === '' || val === null || val === undefined ? undefined : val),
  z.coerce.number().optional()
);

// Helper para campos string opcionais (converte null para undefined)
const optionalString = z.preprocess(
  (val) => (val === null ? undefined : val),
  z.string().optional()
);

const veiculoSchema = z.object({
  marcaId: z.coerce.number().min(1, 'Selecione a marca'),
  modeloId: z.coerce.number().min(1, 'Selecione o modelo'),
  versao: optionalString,
  anoFabricacao: z.coerce.number().min(1900, 'Selecione o ano de fabricação').max(2100),
  anoModelo: z.coerce.number().min(1900, 'Selecione o ano do modelo').max(2100),
  corId: optionalNumber,
  placa: optionalString,
  renavam: optionalString,
  chassi: optionalString,
  km: optionalNumber,
  combustivel: optionalString,
  cambio: optionalString,
  portas: optionalNumber,
  valorCompra: optionalNumber,
  valorVenda: z.coerce.number().min(0.01, 'Informe o valor de venda'),
  valorFipe: optionalNumber,
  dataEntrada: optionalString,
  fornecedorId: optionalNumber,
  observacoes: optionalString,
  status: z.string().default('DISPONIVEL'),
  // Campos técnicos adicionais
  motor: optionalString,
  codigoFipe: optionalString,
  modeloFipe: optionalString,
  especie: optionalString,
  hpCv: optionalNumber,
  numeroMotor: optionalString,
  numeroCambio: optionalString,
});

type VeiculoFormData = z.infer<typeof veiculoSchema>;

interface Marca {
  id: number;
  nome: string;
}

interface Modelo {
  id: number;
  nome: string;
  marcaId: number;
}

interface Cor {
  id: number;
  nome: string;
  codigoHex?: string;
}

interface OpcionalVeiculo {
  id: number;
  nome: string;
  categoria: string;
  icone?: string;
  ordem: number;
  ativo: boolean;
}

export function VeiculoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const isEditing = !!id;

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [marcas, setMarcas] = useState<Marca[]>([]);
  const [modelos, setModelos] = useState<Modelo[]>([]);
  const [cores, setCores] = useState<Cor[]>([]);
  const [fotos, setFotos] = useState<File[]>([]);
  const [fotosPreview, setFotosPreview] = useState<string[]>([]);
  const [opcionais, setOpcionais] = useState<OpcionalVeiculo[]>([]);
  const [selectedOpcionais, setSelectedOpcionais] = useState<Set<number>>(new Set());
  const [dadosIniciais, setDadosIniciais] = useState<Record<string, unknown> | null>(null);
  const [opcionaisIniciais, setOpcionaisIniciais] = useState<number[] | null>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<VeiculoFormData>({
    resolver: zodResolver(veiculoSchema),
    defaultValues: {
      status: 'DISPONIVEL',
    },
  });

  const selectedMarcaId = watch('marcaId');
  const selectedModeloId = watch('modeloId');
  const anoFabricacaoRaw = watch('anoFabricacao');
  const anoModelo = watch('anoModelo');

  // Converter para número (select retorna string)
  const anoFabricacao = anoFabricacaoRaw ? Number(anoFabricacaoRaw) : null;

  // Estado para guardar os valores iniciais durante edição (para resolver timing issues)
  const [initialEditData, setInitialEditData] = useState<{
    modeloId?: number;
    anoModelo?: number;
  } | null>(null);

  // Gerar lista de anos disponíveis (1950 até ano atual + 1)
  const currentYear = new Date().getFullYear();
  const anosDisponiveis = Array.from({ length: currentYear - 1950 + 2 }, (_, i) => currentYear + 1 - i);

  // Opções do Ano Modelo: apenas o mesmo ano ou um ano acima do Ano Fabricação
  const anosModeloDisponiveis = anoFabricacao
    ? [anoFabricacao, anoFabricacao + 1]
    : [];

  // Obter nomes da marca e modelo selecionados
  const marcaNome = marcas.find((m) => m.id === Number(selectedMarcaId))?.nome || '';
  const modeloNome = modelos.find((m) => m.id === Number(selectedModeloId))?.nome || '';

  // Opções de status derivadas de veiculoStatusLabels (evita literais duplicados).
  // VENDIDO é omitido de propósito: a venda passa pelo fluxo de Vendas (VendasService),
  // que cria o registro em `vendas` e mantém data_venda/histórico consistentes.
  // Quando o veículo já está VENDIDO (edição), mantemos a opção apenas para refletir o
  // estado atual, mas desabilitada para impedir alterar a situação por aqui.
  const statusAtual = watch('status');
  const statusOptions = Object.entries(veiculoStatusLabels).filter(
    ([value]) => value !== 'VENDIDO' || statusAtual === 'VENDIDO'
  );

  // Carregar marcas, cores e opcionais
  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      api.get<Marca[]>('/veiculos/marcas', { signal: controller.signal }),
      api.get<Cor[]>('/cores', { signal: controller.signal }),
      api.get<OpcionalVeiculo[]>('/opcionais-veiculo', { signal: controller.signal }),
    ])
      .then(([marcasRes, coresRes, opcionaisRes]) => {
        setMarcas(marcasRes.data);
        setCores(coresRes.data);
        setOpcionais(opcionaisRes.data);
      })
      .catch(() => { /* Ignore abort errors */ });
    return () => controller.abort();
  }, []);

  // Aplicar corId quando cores forem carregadas (resolve race condition)
  useEffect(() => {
    if (dadosIniciais?.corId && cores.length > 0) {
      const corId = dadosIniciais.corId as number;
      if (cores.some(c => c.id === corId)) {
        setValue('corId', corId);
      }
    }
  }, [cores, dadosIniciais, setValue]);

  // Aplicar opcionais quando opcionais forem carregados (resolve race condition)
  useEffect(() => {
    if (opcionaisIniciais && opcionais.length > 0) {
      setSelectedOpcionais(new Set(opcionaisIniciais));
      setOpcionaisIniciais(null); // Limpar para não re-aplicar
    }
  }, [opcionais, opcionaisIniciais]);

  // Carregar modelos quando marca mudar (apenas para criação ou mudança manual pelo usuário)
  useEffect(() => {
    // Não carregar se estamos em modo de edição inicial (será carregado pelo loadVeiculo)
    if (initialEditData) return;
    if (!selectedMarcaId) {
      setModelos([]);
      return;
    }
    const controller = new AbortController();
    api.get<Modelo[]>(`/veiculos/marcas/${selectedMarcaId}/modelos`, { signal: controller.signal })
      .then((response) => {
        setModelos(response.data);
      })
      .catch(() => { /* Ignore abort errors */ });
    return () => controller.abort();
  }, [selectedMarcaId, initialEditData]);

  // Carregar veículo para edição
  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    setIsLoading(true);

    const loadVeiculo = async () => {
      try {
        const response = await api.get<Record<string, unknown>>(`/veiculos/${id}`, { signal: controller.signal });
        const veiculo = response.data;

        // Extrair valores importantes
        const marcaIdValue = veiculo.marcaId as number | undefined;
        const modeloIdValue = veiculo.modeloId as number | undefined;
        const anoFabricacaoValue = veiculo.anoFabricacao as number | undefined;
        const anoModeloValue = veiculo.anoModelo as number | undefined;

        // Marcar que estamos em modo de edição inicial
        setInitialEditData({ modeloId: modeloIdValue, anoModelo: anoModeloValue });

        // Se tem marcaId, carregar modelos PRIMEIRO
        let modelosCarregados: Modelo[] = [];
        if (marcaIdValue) {
          try {
            const modelosRes = await api.get<Modelo[]>(`/veiculos/marcas/${marcaIdValue}/modelos`, { signal: controller.signal });
            modelosCarregados = modelosRes.data;
            setModelos(modelosCarregados);
          } catch {
            // Ignora erro de modelos
          }
        }

        // Guardar corId para aplicar após cores serem carregadas
        const corIdValue = veiculo.corId as number | undefined;
        if (corIdValue) {
          setDadosIniciais({ corId: corIdValue });
        }

        // Definir valores do form EXCETO modeloId, anoModelo e corId (serão definidos após)
        Object.keys(veiculo).forEach((key) => {
          if (key in veiculoSchema.shape && key !== 'modeloId' && key !== 'anoModelo' && key !== 'corId') {
            setValue(key as keyof VeiculoFormData, veiculo[key] as VeiculoFormData[keyof VeiculoFormData]);
          }
        });

        // Carregar fotos existentes
        const fotosData = (veiculo.fotos || veiculo.Fotos) as Array<{ url: string; Url?: string }> | undefined;
        if (fotosData && fotosData.length > 0) {
          const apiBaseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:5000';
          setFotosPreview(fotosData.map((f) => {
            const url = f.url || f.Url || '';
            return url.startsWith('/') ? `${apiBaseUrl}${url}` : url;
          }));
        }

        // Carregar opcionais do veículo e guardar para aplicar após opcionais serem carregados
        try {
          const opcionaisRes = await api.get<number[]>(`/opcionais-veiculo/veiculo/${id}`, { signal: controller.signal });
          const opcionaisDoVeiculo = opcionaisRes.data;
          setOpcionaisIniciais(opcionaisDoVeiculo);
          setSelectedOpcionais(new Set(opcionaisDoVeiculo));
        } catch {
          // Ignora erro de opcionais
        }

        // Aguardar próximo tick para garantir que o React atualizou os selects
        // e depois definir modeloId e anoModelo
        setTimeout(() => {
          // Definir modeloId se os modelos foram carregados
          if (modeloIdValue && modelosCarregados.some(m => m.id === modeloIdValue)) {
            setValue('modeloId', modeloIdValue);
          }

          // Definir anoModelo se anoFabricacao existe
          if (anoModeloValue && anoFabricacaoValue) {
            setValue('anoModelo', anoModeloValue);
          }

          // Limpar flag de edição inicial após mais um delay
          setTimeout(() => {
            setInitialEditData(null);
          }, 100);
        }, 50);

      } catch (error: unknown) {
        const err = error as { name?: string; code?: string; response?: { data?: { message?: string } } };
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        console.error('Erro ao carregar veículo:', error);
        showError(err?.response?.data?.message || 'Erro ao carregar veículo');
      } finally {
        setIsLoading(false);
      }
    };

    loadVeiculo();
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setFotos((prev) => [...prev, ...files]);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFotosPreview((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFoto = (index: number) => {
    setFotos((prev) => prev.filter((_, i) => i !== index));
    setFotosPreview((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: VeiculoFormData) => {
    setIsSaving(true);
    try {
      let veiculoId = id;

      // Preparar payload apenas com campos esperados pelo backend
      const payload = {
        placa: data.placa || null,
        renavam: data.renavam || null,
        chassi: data.chassi || null,
        marcaId: data.marcaId,
        modeloId: data.modeloId,
        versao: data.versao || null,
        anoFabricacao: data.anoFabricacao,
        anoModelo: data.anoModelo,
        corId: data.corId || null,
        combustivel: data.combustivel || null,
        cambio: data.cambio || null,
        portas: data.portas || null,
        km: data.km || 0,
        valorCompra: data.valorCompra || null,
        valorVenda: data.valorVenda,
        valorFipe: data.valorFipe || null,
        fornecedorId: data.fornecedorId || null,
        observacoes: data.observacoes || null,
        status: data.status,
        // Campos técnicos adicionais
        motor: data.motor || null,
        codigoFipe: data.codigoFipe || null,
        modeloFipe: data.modeloFipe || null,
        especie: data.especie || null,
        hpCv: data.hpCv || null,
        numeroMotor: data.numeroMotor || null,
        numeroCambio: data.numeroCambio || null,
      };

      if (isEditing) {
        await api.put(`/veiculos/${id}`, payload);
      } else {
        const response = await api.post('/veiculos', payload);
        veiculoId = response.data.id;
      }

      // Upload fotos - Faz upload para o servidor e depois salva a URL
      if (fotos.length > 0 && veiculoId) {
        for (let i = 0; i < fotos.length; i++) {
          const formData = new FormData();
          formData.append('file', fotos[i]);

          // Upload da imagem para o servidor
          const uploadResponse = await api.post<{ url: string }>('/upload/imagem', formData);

          // Salvar a URL no veículo (primeira foto é principal)
          await api.post(`/veiculos/${veiculoId}/midias/fotos`, {
            url: uploadResponse.data.url,
            principal: i === 0
          });
        }
      }

      // Salvar opcionais do veículo
      if (veiculoId) {
        await api.put(`/opcionais-veiculo/veiculo/${veiculoId}`, Array.from(selectedOpcionais));
      }

      showSuccess(isEditing ? 'Veículo atualizado com sucesso' : 'Veículo cadastrado com sucesso');
      navigate('/veiculos');
    } catch (error: unknown) {
      console.error('Erro ao salvar veículo:', error);
      const err = error as { response?: { data?: { message?: string; title?: string; errors?: Record<string, string[]> } } };
      let message = 'Erro ao salvar veículo';
      if (err?.response?.data?.message) {
        message = err.response.data.message;
      } else if (err?.response?.data?.title) {
        message = err.response.data.title;
        // Se tiver erros detalhados, mostrar o primeiro
        if (err.response.data.errors) {
          const firstError = Object.values(err.response.data.errors)[0];
          if (firstError?.[0]) {
            message += `: ${firstError[0]}`;
          }
        }
      }
      showError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={isEditing ? 'Editar Veículo' : 'Novo Veículo'}
        subtitle={isEditing ? 'Altere os dados do veículo' : 'Cadastre um novo veículo no estoque'}
        showBack
        backTo="/veiculos"
      />

      <form onSubmit={handleSubmit(onSubmit, (validationErrors) => {
        console.log('Erros de validação:', validationErrors);
        // Mostrar o primeiro erro encontrado
        const firstError = Object.values(validationErrors)[0];
        if (firstError?.message) {
          showError(`Erro de validação: ${firstError.message}`);
        }
      })} className="space-y-6">
        {/* Mostrar resumo de erros se houver */}
        {Object.keys(errors).length > 0 && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
            <p className="text-rose-800 font-medium mb-2">Corrija os erros abaixo:</p>
            <ul className="list-disc list-inside text-sm text-rose-700">
              {Object.entries(errors).map(([field, error]) => (
                <li key={field}>{field}: {error?.message}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Fotos */}
        <Panel title="Fotos">
          <div className="flex flex-wrap gap-4">
            {fotosPreview.map((preview, index) => (
              <div key={index} className="relative w-32 h-32">
                <img
                  src={preview}
                  alt={`Foto ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeFoto(index)}
                  className="absolute -top-2 -right-2 p-1 bg-rose-500 text-white rounded-full hover:bg-rose-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <label className="w-32 h-32 flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors">
              <Camera className="w-8 h-8 text-slate-400" />
              <span className="text-xs text-slate-500 mt-1">Adicionar</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFotoChange}
                className="hidden"
              />
            </label>
          </div>
        </Panel>

        {/* Dados do Veículo */}
        <Panel title="Dados do Veículo">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>
                Marca *
              </label>
              <select
                {...register('marcaId')}
                className={inputClass}
              >
                <option value="">Selecione...</option>
                {marcas.map((marca) => (
                  <option key={marca.id} value={marca.id}>
                    {marca.nome}
                  </option>
                ))}
              </select>
              {errors.marcaId && (
                <p className="text-sm text-rose-600 mt-1">{errors.marcaId.message}</p>
              )}
            </div>

            <div>
              <label className={labelClass}>
                Modelo *
              </label>
              <select
                {...register('modeloId')}
                disabled={!selectedMarcaId}
                className={`${inputClass} disabled:bg-slate-100`}
              >
                <option value="">Selecione...</option>
                {modelos.map((modelo) => (
                  <option key={modelo.id} value={modelo.id}>
                    {modelo.nome}
                  </option>
                ))}
              </select>
              {errors.modeloId && (
                <p className="text-sm text-rose-600 mt-1">{errors.modeloId.message}</p>
              )}
            </div>

            <div>
              <label className={labelClass}>
                Versão
              </label>
              <input
                type="text"
                {...register('versao')}
                placeholder="Ex: LT 1.0"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>
                Ano Fabricação *
              </label>
              <select
                {...register('anoFabricacao', {
                  onChange: (e) => {
                    const ano = Number(e.target.value);
                    // Se ano modelo atual não for válido para o novo ano fabricação, ajustar
                    if (anoModelo && (anoModelo < ano || anoModelo > ano + 1)) {
                      setValue('anoModelo', ano);
                    }
                  }
                })}
                className={inputClass}
              >
                <option value="">Selecione...</option>
                {anosDisponiveis.map((ano) => (
                  <option key={ano} value={ano}>
                    {ano}
                  </option>
                ))}
              </select>
              {errors.anoFabricacao && (
                <p className="text-sm text-rose-600 mt-1">{errors.anoFabricacao.message}</p>
              )}
            </div>

            <div>
              <label className={labelClass}>
                Ano Modelo *
              </label>
              <select
                {...register('anoModelo')}
                disabled={!anoFabricacao}
                className={`${inputClass} disabled:bg-slate-100 disabled:cursor-not-allowed`}
              >
                <option value="">Selecione...</option>
                {anosModeloDisponiveis.map((ano) => (
                  <option key={ano} value={ano}>
                    {ano}
                  </option>
                ))}
              </select>
              {errors.anoModelo && (
                <p className="text-sm text-rose-600 mt-1">{errors.anoModelo.message}</p>
              )}
            </div>

            <div>
              <label className={labelClass}>
                Cor
              </label>
              <select
                {...register('corId')}
                className={inputClass}
              >
                <option value="">Selecione...</option>
                {cores.map((cor) => (
                  <option key={cor.id} value={cor.id}>
                    {cor.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>
                Placa
              </label>
              <input
                type="text"
                {...register('placa')}
                placeholder="ABC1D23"
                maxLength={7}
                className={`${inputClass} uppercase`}
              />
            </div>

            <div>
              <label className={labelClass}>
                Chassi
              </label>
              <input
                type="text"
                {...register('chassi')}
                placeholder="9BWZZZ377VT004251"
                maxLength={17}
                className={`${inputClass} uppercase`}
              />
            </div>

            <div>
              <label className={labelClass}>
                Renavam
              </label>
              <input
                type="text"
                {...register('renavam')}
                placeholder="01234567890"
                maxLength={11}
                className={inputClass}
              />
            </div>

            <Controller
              name="km"
              control={control}
              render={({ field }) => (
                <InputInteger
                  label="Quilometragem"
                  suffix=" km"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />

            <div>
              <label className={labelClass}>
                Combustível
              </label>
              <select
                {...register('combustivel')}
                className={inputClass}
              >
                <option value="">Selecione...</option>
                <option value="FLEX">Flex</option>
                <option value="GASOLINA">Gasolina</option>
                <option value="ETANOL">Etanol</option>
                <option value="DIESEL">Diesel</option>
                <option value="GNV">GNV</option>
                <option value="ELETRICO">Elétrico</option>
                <option value="HIBRIDO">Híbrido</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>
                Câmbio
              </label>
              <select
                {...register('cambio')}
                className={inputClass}
              >
                <option value="">Selecione...</option>
                <option value="MANUAL">Manual</option>
                <option value="AUTOMATICO">Automático</option>
                <option value="AUTOMATIZADO">Automatizado</option>
                <option value="CVT">CVT</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>
                Portas
              </label>
              <select
                {...register('portas')}
                className={inputClass}
              >
                <option value="">Selecione...</option>
                <option value="2">2 portas</option>
                <option value="4">4 portas</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>
                Status
              </label>
              <select
                {...register('status')}
                className={inputClass}
              >
                {statusOptions.map(([value, label]) => (
                  <option key={value} value={value} disabled={value === 'VENDIDO'}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Panel>

        {/* Valores */}
        <Panel title="Valores">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Controller
              name="valorCompra"
              control={control}
              render={({ field }) => (
                <InputDecimal
                  label="Valor de Compra"
                  prefix="R$ "
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />

            <Controller
              name="valorVenda"
              control={control}
              render={({ field }) => (
                <InputDecimal
                  label="Valor de Venda *"
                  prefix="R$ "
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.valorVenda?.message}
                />
              )}
            />

            <Controller
              name="valorFipe"
              control={control}
              render={({ field }) => (
                <FipeSelector
                  label="Valor FIPE"
                  marcaNome={marcaNome}
                  modeloNome={modeloNome}
                  anoModelo={anoModelo}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </Panel>

        {/* Dados Técnicos */}
        <Panel title="Dados Técnicos">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>
                Motorização
              </label>
              <input
                type="text"
                {...register('motor')}
                placeholder="Ex: 1.0, 1.6, 2.0"
                className={inputClass}
              />
            </div>

            <Controller
              name="hpCv"
              control={control}
              render={({ field }) => (
                <InputInteger
                  label="Potência (HP/CV)"
                  suffix=" cv"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />

            <div>
              <label className={labelClass}>
                Espécie
              </label>
              <select
                {...register('especie')}
                className={inputClass}
              >
                <option value="">Selecione...</option>
                <option value="AUTOMOVEL">Automóvel</option>
                <option value="MOTOCICLETA">Motocicleta</option>
                <option value="CAMINHONETE">Caminhonete</option>
                <option value="CAMINHAO">Caminhão</option>
                <option value="UTILITARIO">Utilitário</option>
                <option value="OUTROS">Outros</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>
                Código FIPE
              </label>
              <input
                type="text"
                {...register('codigoFipe')}
                placeholder="Ex: 008247-3"
                className={inputClass}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>
                Modelo FIPE
              </label>
              <input
                type="text"
                {...register('modeloFipe')}
                placeholder="Ex: A4 Prestige 2.0 TFSI 190cv S-tronic"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>
                Número do Motor
              </label>
              <input
                type="text"
                {...register('numeroMotor')}
                placeholder="Número gravado no motor"
                className={`${inputClass} uppercase`}
              />
            </div>

            <div>
              <label className={labelClass}>
                Número do Câmbio
              </label>
              <input
                type="text"
                {...register('numeroCambio')}
                placeholder="Número gravado no câmbio"
                className={`${inputClass} uppercase`}
              />
            </div>
          </div>
        </Panel>

        {/* Observações */}
        <Panel title="Observações">
          <textarea
            {...register('observacoes')}
            rows={4}
            placeholder="Observações sobre o veículo..."
            className={`${inputClass} h-auto py-2 resize-none`}
          />
        </Panel>

        {/* Opcionais */}
        {opcionais.length > 0 && (
          <Panel title="Opcionais">
            {/* Agrupar por categoria */}
            {Object.entries(
              opcionais.reduce((acc, op) => {
                const cat = op.categoria || 'OUTROS';
                if (!acc[cat]) acc[cat] = [];
                acc[cat].push(op);
                return acc;
              }, {} as Record<string, OpcionalVeiculo[]>)
            ).map(([categoria, items]) => {
              const allSelected = items.every((op) => selectedOpcionais.has(op.id));
              const someSelected = items.some((op) => selectedOpcionais.has(op.id));

              const handleSelectAll = () => {
                const newSet = new Set(selectedOpcionais);
                if (allSelected) {
                  // Desmarcar todos da categoria
                  items.forEach((op) => newSet.delete(op.id));
                } else {
                  // Marcar todos da categoria
                  items.forEach((op) => newSet.add(op.id));
                }
                setSelectedOpcionais(newSet);
              };

              return (
                <div key={categoria} className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-slate-700">{categoria}</h3>
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className={`text-xs px-2 py-1 rounded-lg transition-colors ${
                        allSelected
                          ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                          : someSelected
                          ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {allSelected ? 'Desmarcar Todos' : 'Selecionar Todos'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {items.map((opcional) => (
                      <label
                        key={opcional.id}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                          selectedOpcionais.has(opcional.id)
                            ? 'bg-primary-50 border-primary-500 text-primary-700'
                            : 'bg-white border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedOpcionais.has(opcional.id)}
                          onChange={(e) => {
                            const newSet = new Set(selectedOpcionais);
                            if (e.target.checked) {
                              newSet.add(opcional.id);
                            } else {
                              newSet.delete(opcional.id);
                            }
                            setSelectedOpcionais(newSet);
                          }}
                          className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm">{opcional.nome}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
            <div className="mt-2 text-sm text-slate-500">
              {selectedOpcionais.size} opcional(is) selecionado(s)
            </div>
          </Panel>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/veiculos')}
            className={buttonClass('secondary')}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className={buttonClass('primary')}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isSaving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
}
